import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { accountsAPI, unwrapVendors, unwrapBills, unwrapPayments, normalizeVendor } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  ArrowLeft, Building2, FileText, CreditCard, Plus, Loader2,
  IndianRupee, Calendar, CheckCircle2, FileSpreadsheet, FileDown,
  Trash2, AlertTriangle, Paperclip
} from 'lucide-react';
import { toast } from 'sonner';
import PaymentAllocationDialog from '../components/PaymentAllocationDialog';
import BulkPaymentDialog from '../components/BulkPaymentDialog';
import PeriodToolbar from '../components/PeriodToolbar';
import { usePeriod } from '../hooks/usePeriod';
import { PERIOD_MODES, businessToday } from '../lib/period';
import { newPaymentKey, paymentCommandError } from '../lib/paymentCommands';

const formatCurrency = (value) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(value || 0);
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const StatusBadge = ({ status }) => {
  const variants = {
    'Unpaid': 'bg-rose-500/10 text-rose-400 border-rose-500/20',
    'Partial': 'bg-amber-500/10 text-amber-400 border-amber-500/20',
    'Paid': 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
  };

  return (
    <Badge variant="outline" className={`${variants[status] || variants['Unpaid']} font-medium`}>
      {status}
    </Badge>
  );
};

const VendorDetailPage = () => {
  const { vendorId } = useParams();
  const navigate = useNavigate();
  const [vendor, setVendor] = useState(null);
  const [bills, setBills] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('bills');

  // Shared Accounts period — scopes bills, summary cards, payments and ledger.
  const periodState = usePeriod(PERIOD_MODES.RANGE_ALL_TIME);
  const periodParams = periodState.apiParams;
  const [resolvedPeriod, setResolvedPeriod] = useState(null);
  
  // Payment dialog state — now bill-scoped per Phase 3 source hardening
  const paymentKeyRef = useRef(null);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [selectedBill, setSelectedBill] = useState(null);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('BANK_TRANSFER');
  const [paymentRef, setPaymentRef] = useState('');
  const [paymentDate, setPaymentDate] = useState(businessToday());
  const [paymentNotes, setPaymentNotes] = useState('');
  const [creatingPayment, setCreatingPayment] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  
  // Allocation state (unified dialog)
  const [allocationDialogOpen, setAllocationDialogOpen] = useState(false);
  const [currentPayment, setCurrentPayment] = useState(null);

  // Delete payment state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [paymentToDelete, setPaymentToDelete] = useState(null);
  const [deleteReason, setDeleteReason] = useState('');
  const [deleting, setDeleting] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [vendorRes, billsRes, paymentsRes] = await Promise.all([
        accountsAPI.getVendor(vendorId),
        accountsAPI.getVendorBills(vendorId, periodParams),
        accountsAPI.getVendorPayments(vendorId, periodParams),
      ]);
      setResolvedPeriod(billsRes.data);
      setVendor(normalizeVendor(vendorRes.data));
      setBills(unwrapBills(billsRes.data));
      setPayments(unwrapPayments(paymentsRes.data));
    } catch (err) {
      toast.error('Failed to load vendor data');
    }
    setLoading(false);
  }, [vendorId, periodParams]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Turn any backend error into a plain string. The Phase 3 410 endpoints
  // return `detail` as a dict ({decommissioned, reason, use_instead}) which
  // used to render as "[object Object]" in toasts — this helper flattens.
  const extractErrorDetail = (err, fallback = 'Request failed') => {
    const detail = err?.response?.data?.detail;
    if (!detail) return err?.message || fallback;
    if (typeof detail === 'string') return detail;
    if (typeof detail === 'object') {
      if (detail.use_instead) {
        return `${detail.reason || 'This endpoint has moved.'} Use: ${detail.use_instead}`;
      }
      return detail.reason || detail.message || detail.code || JSON.stringify(detail);
    }
    return String(detail);
  };

  const openBillPaymentDialog = (bill) => {
    setSelectedBill(bill);
    const balance = Math.max(
      Number(bill.total_amount || bill.amount || 0) - Number(bill.amount_paid || 0),
      0,
    );
    setPaymentAmount(String(balance || ''));
    setPaymentMethod('BANK_TRANSFER');
    setPaymentRef('');
    setPaymentDate(businessToday());
    setPaymentNotes('');
    paymentKeyRef.current = newPaymentKey();
    setPaymentDialogOpen(true);
  };

  const handleMarkBillPaid = async (confirmRepeat = false) => {
    if (!selectedBill) return;
    const amt = parseFloat(paymentAmount);
    if (!amt || amt <= 0) {
      toast.error('Enter a valid amount');
      return;
    }
    const billTotal = Number(selectedBill.total_amount || selectedBill.amount || 0);
    const alreadyPaid = Number(selectedBill.amount_paid || 0);
    const cumulative = alreadyPaid + amt;
    // Bill-bound semantics: PAID when cumulative payment >= net; PARTIAL otherwise.
    const paymentStatus = cumulative + 0.01 >= billTotal ? 'Paid' : 'Partial';

    setCreatingPayment(true);
    try {
      await accountsAPI.markBillPaid(selectedBill.bill_id || selectedBill.id, {
        payment_status: paymentStatus,
        amount_paid: amt,
        payment_reference: paymentRef || null,
        payment_method: paymentMethod || null,
        payment_date: paymentDate || null,
        notes: paymentNotes || null,
        paid_at: paymentDate ? new Date(paymentDate).toISOString() : undefined,
        confirm_repeat: confirmRepeat === true,
      }, paymentKeyRef.current);
      toast.success(
        paymentStatus === 'Paid'
          ? `Bill marked Paid (${formatCurrency(amt)})`
          : `Partial payment of ${formatCurrency(amt)} recorded`,
      );
      setPaymentDialogOpen(false);
      setSelectedBill(null);
      setPaymentAmount('');
      setPaymentRef('');
      setPaymentNotes('');
      loadData();
    } catch (err) {
      const e = paymentCommandError(err, extractErrorDetail(err, 'Failed to record payment against bill'));
      if (e.code === 'SUSPECTED_REPEAT') {
        toast.warning(e.message, { action: { label: 'Record anyway', onClick: () => handleMarkBillPaid(true) }, duration: 15000 });
      } else {
        toast.error(`${e.title}: ${e.message}`, { duration: e.code === 'COMMAND_OUTCOME_UNKNOWN' ? 15000 : 6000 });
      }
    } finally {
      setCreatingPayment(false);
    }
  };

  const handleDeletePayment = async () => {
    // Phase 3 (May 2026): DELETE /accounts/payments/{id} is 410 Gone.
    // Payment truth lives in Expense Capture; local audit rows in
    // sync_back_events must not be tampered with. Surface the fact
    // cleanly instead of firing a request that will fail.
    toast.error(
      'Payment audit rows cannot be deleted from Accounts. Payment truth is owned by Expense Capture — reverse the payment there if needed.',
    );
    setDeleteDialogOpen(false);
    setPaymentToDelete(null);
    setDeleteReason('');
  };

  const openDeleteDialog = (payment) => {
    setPaymentToDelete(payment);
    setDeleteReason('');
    setDeleteDialogOpen(true);
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-48" />
        <div className="grid grid-cols-3 gap-4">
          <Skeleton className="h-32" />
          <Skeleton className="h-32" />
          <Skeleton className="h-32" />
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  if (!vendor) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        Vendor not found
      </div>
    );
  }

  // Filtered summary — computed from the bill rows currently returned
  // by the server (already scoped to the selected date_from/date_to).
  // When no filter is active, we fall back to vendor.bills_summary
  // which is the lifetime aggregate coming from Expense Capture.
  const filteredSummary = (() => {
    if (!bills || bills.length === 0) {
      return { total_billed: 0, total_paid: 0, outstanding: 0, bill_count: 0, unpaid_count: 0, partial_count: 0 };
    }
    let total_billed = 0, total_paid = 0, unpaid_count = 0, partial_count = 0, dup_hidden = 0;
    for (const b of bills) {
      // Duplicate containment — HARD_DUPLICATE rows stay in the table
      // (visible with a badge) but are excluded from financial totals so
      // Total Billed / Outstanding reflect the canonical payable.
      if (b._excluded_from_totals || b.duplicate_state === 'HARD_DUPLICATE') {
        dup_hidden += 1;
        continue;
      }
      total_billed += Number(b.total_amount || b.amount || 0);
      total_paid += Number(b.paid_amount || 0);
      const st = (b.payment_status || '').toString();
      if (st === 'Unpaid') unpaid_count += 1;
      else if (st === 'Partial') partial_count += 1;
    }
    return {
      total_billed,
      total_paid,
      outstanding: Math.max(total_billed - total_paid, 0),
      bill_count: bills.length - dup_hidden,
      unpaid_count,
      partial_count,
      dup_hidden,
    };
  })();

  // Summary = exactly the bills returned for the active period (duplicates excluded).
  const summary = filteredSummary;

  return (
    <div className="space-y-6" data-testid="vendor-detail-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/vendors')}>
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-manrope font-bold tracking-tight flex items-center gap-3">
            <Building2 className="w-6 h-6 text-emerald-400" />
            {vendor.name}
          </h1>
          <div className="text-muted-foreground mt-1 flex items-center gap-2">
            {vendor.category && <Badge variant="outline">{vendor.category}</Badge>}
            {vendor.gstin && <span className="font-mono text-sm">GSTIN: {vendor.gstin}</span>}
          </div>
        </div>
        {/* Vendor-level "Record Payment" was removed in Phase 3 source
            hardening (May 2026). Payments are now recorded per-bill via
            the "Mark Paid" button on each row in the Bills tab. */}
      </div>

      {/* Date-range filter (same component/pattern as Bills page).
          Applies to the Bills tab + summary cards. Payments and Ledger
          tabs deliberately unaffected — their timestamps are not
          semantically compatible with bill_date. */}
      <Card className="bg-card/50 backdrop-blur-sm border-border/50">
        <CardContent className="p-4 flex flex-col md:flex-row md:items-center gap-3">
          <PeriodToolbar state={periodState} mode={PERIOD_MODES.RANGE_ALL_TIME} resolved={resolvedPeriod} />
          <span className="text-xs text-muted-foreground" data-testid="vendor-filter-summary">
            Bills, summary, payments &amp; ledger use this period
          </span>
          <Button
            size="sm"
            variant="outline"
            className="text-xs h-8 ml-auto border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10"
            onClick={() => setBulkOpen(true)}
            data-testid="bulk-payment-btn"
            disabled={!bills.some((b) => ['Unpaid', 'Partial', 'Partially Paid'].includes(b.payment_status) && b.duplicate_state !== 'HARD_DUPLICATE')}
          >
            Record Bulk Payment
          </Button>
        </CardContent>
      </Card>

      {/* Summary Cards — respect the active date-range filter */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="vendor-summary-cards">
        <Card className="bg-card/50 backdrop-blur-sm border-border/50">
          <CardContent className="p-6">
            <p className="text-sm text-muted-foreground">
              Total Billed
            </p>
            <p className="text-2xl font-mono font-semibold mt-1" data-testid="summary-total-billed">
              {formatCurrency(summary.total_billed)}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              {summary.bill_count} bill{summary.bill_count === 1 ? '' : 's'}
              {summary.dup_hidden > 0 && (
                <span className="ml-2 text-amber-300" data-testid="summary-dup-hidden">
                  · {summary.dup_hidden} duplicate{summary.dup_hidden === 1 ? '' : 's'} hidden
                </span>
              )}
            </p>
          </CardContent>
        </Card>
        <Card className="bg-card/50 backdrop-blur-sm border-emerald-500/30">
          <CardContent className="p-6">
            <p className="text-sm text-muted-foreground">
              Total Paid
            </p>
            <p className="text-2xl font-mono font-semibold text-emerald-400 mt-1" data-testid="summary-total-paid">
              {formatCurrency(summary.total_paid)}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              Paid amounts on bills in this period
            </p>
          </CardContent>
        </Card>
        <Card className={`bg-card/50 backdrop-blur-sm ${summary.outstanding > 0 ? 'border-amber-500/30' : 'border-emerald-500/30'}`}>
          <CardContent className="p-6">
            <p className="text-sm text-muted-foreground">
              Outstanding
            </p>
            <p className={`text-2xl font-mono font-semibold mt-1 ${summary.outstanding > 0 ? 'text-amber-400' : 'text-emerald-400'}`} data-testid="summary-outstanding">
              {formatCurrency(summary.outstanding)}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              {summary.unpaid_count} unpaid, {summary.partial_count} partial
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-border/50 pb-2">
        {['bills', 'payments', 'ledger'].map((tab) => (
          <Button
            key={tab}
            variant={activeTab === tab ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setActiveTab(tab)}
            className={activeTab === tab ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
          >
            {tab === 'bills' && <FileText className="w-4 h-4 mr-2" />}
            {tab === 'payments' && <CreditCard className="w-4 h-4 mr-2" />}
            {tab === 'ledger' && <IndianRupee className="w-4 h-4 mr-2" />}
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
          </Button>
        ))}
      </div>

      {/* Bills Tab */}
      {activeTab === 'bills' && (
        <Card className="bg-card/50 backdrop-blur-sm border-border/50">
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className="border-border/50 hover:bg-transparent">
                  <TableHead>Bill Number</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead className="text-right">Paid</TableHead>
                  <TableHead className="text-right">Outstanding</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {bills.map((bill) => {
                  const outstanding =
                    bill.outstanding_amount ?? (Number(bill.total_amount || 0) - Number(bill.paid_amount || 0));
                  const isFullyPaid = outstanding <= 0.01;
                  return (
                    <TableRow key={bill.bill_id} className={`border-border/50 hover:bg-muted/50 ${bill.duplicate_state === 'HARD_DUPLICATE' ? 'opacity-60' : ''}`} data-testid={`bill-row-${bill.bill_id}`}>
                      <TableCell className="font-mono text-sm">
                        {bill.bill_number}
                        {bill.duplicate_state === 'HARD_DUPLICATE' && (
                          <span className="ml-2 text-[9px] px-1.5 py-0.5 rounded border border-amber-500/40 bg-amber-500/10 text-amber-300" data-testid={`dup-badge-${bill.bill_id}`} title={`Hidden from totals · canonical=${bill.duplicate_of?.slice(0,8) || '—'}`}>DUP</span>
                        )}
                      </TableCell>
                      <TableCell className="font-mono text-sm">{formatDate(bill.bill_date)}</TableCell>
                      <TableCell className="text-right font-mono">{formatCurrency(bill.total_amount)}</TableCell>
                      <TableCell className="text-right font-mono text-emerald-400">{formatCurrency(bill.paid_amount || 0)}</TableCell>
                      <TableCell className="text-right font-mono text-amber-400">{formatCurrency(outstanding)}</TableCell>
                      <TableCell><StatusBadge status={bill.payment_status} /></TableCell>
                      <TableCell className="text-right">
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={isFullyPaid}
                          onClick={() => openBillPaymentDialog({ ...bill, amount_paid: bill.paid_amount || 0 })}
                          className="h-8 text-xs border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 disabled:opacity-40"
                          data-testid={`mark-bill-paid-btn-${bill.bill_id}`}
                        >
                          <CreditCard className="w-3 h-3 mr-1" />
                          {isFullyPaid ? 'Paid' : (bill.paid_amount > 0 ? 'Add Payment' : 'Mark Paid')}
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {bills.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                      No bills found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Payments Tab */}
      {activeTab === 'payments' && (
        <Card className="bg-card/50 backdrop-blur-sm border-border/50">
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className="border-border/50 hover:bg-transparent">
                  <TableHead>Date</TableHead>
                  <TableHead>Reference</TableHead>
                  <TableHead>Method</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead className="text-right">Allocated</TableHead>
                  <TableHead className="text-right">Unallocated</TableHead>
                  <TableHead className="text-center">Proof</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {payments.map((payment) => (
                  <TableRow key={payment.payment_id} className="border-border/50 hover:bg-muted/50" data-testid={`payment-row-${payment.payment_id}`}>
                    <TableCell className="font-mono text-sm">{formatDate(payment.payment_date)}</TableCell>
                    <TableCell className="font-mono text-sm">{payment.reference_no || '-'}</TableCell>
                    <TableCell><Badge variant="outline">{payment.payment_method}</Badge></TableCell>
                    <TableCell>
                      {payment.auto_created ? (
                        <Badge variant="outline" className="bg-blue-500/10 text-blue-400 border-blue-500/20 text-xs">AUTO</Badge>
                      ) : (
                        <Badge variant="outline" className="text-xs">MANUAL</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right font-mono">{formatCurrency(payment.amount)}</TableCell>
                    <TableCell className="text-right font-mono text-emerald-400">{formatCurrency(payment.allocated_amount || 0)}</TableCell>
                    <TableCell className="text-right font-mono text-amber-400">{formatCurrency(payment.unallocated_amount || 0)}</TableCell>
                    <TableCell className="text-center">
                      {payment.proof_status === 'ATTACHED' ? (
                        <Badge variant="outline" className="bg-emerald-500/10 text-emerald-400 border-emerald-500/20 text-[10px] cursor-pointer" data-testid={`proof-badge-${payment.payment_id}`}>
                          <Paperclip className="w-3 h-3 mr-1" />
                          Attached
                        </Badge>
                      ) : (
                        <span className="text-[10px] text-zinc-500">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-rose-400 hover:text-rose-300 hover:bg-rose-500/10 h-8 w-8 p-0"
                        onClick={() => openDeleteDialog(payment)}
                        data-testid={`delete-payment-btn-${payment.payment_id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {payments.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                      No payments found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Ledger Tab */}
      {activeTab === 'ledger' && (
        <LedgerView vendorId={vendorId} periodParams={periodParams} />
      )}

      {/* Mark Bill Paid Dialog — bill-scoped payment record per Phase 3
          source hardening. Writes go through the EC mark-paid callback
          only, no local payments ledger. */}
      <Dialog open={paymentDialogOpen} onOpenChange={setPaymentDialogOpen}>
        <DialogContent className="bg-card border-border" data-testid="payment-dialog">
          <DialogHeader>
            <DialogTitle className="font-manrope">
              {selectedBill?.paid_amount > 0 ? 'Record Payment Against Bill' : 'Mark Bill Paid'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {selectedBill && (
              <div
                className="rounded-md border border-border/50 bg-muted/30 p-3 text-xs"
                data-testid="payment-dialog-bill-context"
              >
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Bill</span>
                  <span className="font-mono text-foreground">
                    {selectedBill.bill_number || selectedBill.bill_id?.slice(0, 8)}
                  </span>
                </div>
                <div className="flex items-center justify-between mt-1">
                  <span className="text-muted-foreground">Total</span>
                  <span className="font-mono text-foreground">
                    {formatCurrency(selectedBill.total_amount || selectedBill.amount || 0)}
                  </span>
                </div>
                <div className="flex items-center justify-between mt-1">
                  <span className="text-muted-foreground">Already paid</span>
                  <span className="font-mono text-emerald-400">
                    {formatCurrency(selectedBill.amount_paid || 0)}
                  </span>
                </div>
                <div className="flex items-center justify-between mt-1 pt-1 border-t border-border/50">
                  <span className="text-muted-foreground">Balance</span>
                  <span className="font-mono text-amber-300">
                    {formatCurrency(
                      Math.max(
                        Number(selectedBill.total_amount || selectedBill.amount || 0)
                          - Number(selectedBill.amount_paid || 0),
                        0,
                      ),
                    )}
                  </span>
                </div>
              </div>
            )}
            <div className="space-y-2">
              <label className="text-sm font-medium">Amount (₹)</label>
              <Input
                type="number"
                value={paymentAmount}
                onChange={(e) => setPaymentAmount(e.target.value)}
                placeholder="Enter amount"
                className="bg-background border-border/50"
                data-testid="payment-amount-input"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Payment Method</label>
              <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                <SelectTrigger className="bg-background border-border/50" data-testid="payment-method-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="BANK_TRANSFER">Bank Transfer</SelectItem>
                  <SelectItem value="UPI">UPI</SelectItem>
                  <SelectItem value="CHEQUE">Cheque</SelectItem>
                  <SelectItem value="CASH">Cash</SelectItem>
                  <SelectItem value="CARD">Card</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Reference Number</label>
              <Input
                value={paymentRef}
                onChange={(e) => setPaymentRef(e.target.value)}
                placeholder="Transaction ID / Cheque No"
                className="bg-background border-border/50"
                data-testid="payment-reference-input"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Payment Date</label>
              <Input
                type="date"
                value={paymentDate}
                onChange={(e) => setPaymentDate(e.target.value)}
                className="bg-background border-border/50"
                data-testid="payment-date-input"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Notes (optional)</label>
              <Input
                value={paymentNotes}
                onChange={(e) => setPaymentNotes(e.target.value)}
                placeholder="Any additional context"
                className="bg-background border-border/50"
                data-testid="payment-notes-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPaymentDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={handleMarkBillPaid}
              className="bg-emerald-600 hover:bg-emerald-700"
              disabled={creatingPayment || !paymentAmount}
              data-testid="create-payment-btn"
            >
              {creatingPayment ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {(() => {
                const amt = parseFloat(paymentAmount) || 0;
                const bal = Math.max(
                  Number(selectedBill?.total_amount || selectedBill?.amount || 0)
                    - Number(selectedBill?.amount_paid || 0),
                  0,
                );
                return amt + 0.01 >= bal && bal > 0 ? 'Mark Paid' : 'Record Partial Payment';
              })()}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Unified Payment Allocation Dialog */}
      <PaymentAllocationDialog
        open={allocationDialogOpen}
        onOpenChange={setAllocationDialogOpen}
        payment={currentPayment}
        vendorId={vendorId}
        onComplete={loadData}
      />

      {/* Delete Payment Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="bg-card border-border" data-testid="delete-payment-dialog">
          <DialogHeader>
            <DialogTitle className="font-manrope flex items-center gap-2 text-rose-400">
              <AlertTriangle className="w-5 h-5" />
              Delete Payment
            </DialogTitle>
          </DialogHeader>
          {paymentToDelete && (
            <div className="space-y-4 py-2">
              <div className="bg-rose-500/5 border border-rose-500/20 rounded-lg p-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Amount</span>
                  <span className="font-mono font-semibold text-rose-400">{formatCurrency(paymentToDelete.amount)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Date</span>
                  <span className="font-mono">{formatDate(paymentToDelete.payment_date)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Method</span>
                  <span>{paymentToDelete.payment_method}</span>
                </div>
                {paymentToDelete.auto_created && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Source</span>
                    <Badge variant="outline" className="bg-blue-500/10 text-blue-400 border-blue-500/20 text-xs">AUTO-CREATED</Badge>
                  </div>
                )}
                {(paymentToDelete.allocated_amount || 0) > 0 && (
                  <div className="mt-2 pt-2 border-t border-rose-500/10">
                    <p className="text-xs text-rose-300">
                      This payment has {formatCurrency(paymentToDelete.allocated_amount)} allocated to bills.
                      Deleting will reverse all allocations and update bill statuses.
                    </p>
                  </div>
                )}
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Reason for deletion</label>
                <Input
                  value={deleteReason}
                  onChange={(e) => setDeleteReason(e.target.value)}
                  placeholder="e.g., Duplicate entry, Incorrect amount, Wrong vendor..."
                  className="bg-background border-border/50"
                  data-testid="delete-reason-input"
                />
              </div>
              <p className="text-xs text-muted-foreground">
                This action is recorded in the audit log with a full snapshot of the payment for traceability.
              </p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)} data-testid="cancel-delete-btn">
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeletePayment}
              disabled={deleting || !deleteReason.trim()}
              data-testid="confirm-delete-payment-btn"
            >
              {deleting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Trash2 className="w-4 h-4 mr-2" />}
              Delete Payment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Payment Dialog — one shared payment_reference across N
          bill-scoped mark-paid callbacks. Idempotent + partial-failure
          safe (see /api/accounts/expenses/bills/bulk-mark-paid). */}
      <BulkPaymentDialog
        open={bulkOpen}
        onClose={() => setBulkOpen(false)}
        bills={bills}
        onDone={() => {
          setBulkOpen(false);
          loadData();
        }}
      />
    </div>
  );
};

// Ledger View Component
const LedgerView = ({ vendorId, periodParams }) => {
  const [ledger, setLedger] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(null);

  const loadLedger = useCallback(async () => {
    try {
      const response = await accountsAPI.getVendorLedger(vendorId, periodParams);
      setLedger(response.data);
    } catch (err) {
      toast.error('Failed to load ledger');
    }
    setLoading(false);
  }, [vendorId, periodParams]);

  useEffect(() => {
    loadLedger();
  }, [loadLedger]);

  const handleExport = async (format) => {
    setExporting(format);
    try {
      let response;
      let filename;
      let mimeType;
      
      if (format === 'csv') {
        response = await accountsAPI.exportVendorLedgerCSV(vendorId, periodParams);
        filename = `vendor_ledger_${vendorId.substring(0, 8)}_${businessToday()}.csv`;
        mimeType = 'text/csv';
      } else if (format === 'xlsx') {
        response = await accountsAPI.exportVendorLedgerXLSX(vendorId, periodParams);
        filename = `vendor_ledger_${vendorId.substring(0, 8)}_${businessToday()}.xlsx`;
        mimeType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      } else if (format === 'pdf') {
        response = await accountsAPI.exportVendorLedgerPDF(vendorId, periodParams);
        filename = `vendor_ledger_${vendorId.substring(0, 8)}_${businessToday()}.html`;
        mimeType = 'text/html';
      }
      
      // Create download link
      const blob = new Blob([response.data], { type: mimeType });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('Export failed');
    }
    setExporting(null);
  };

  if (loading) return <Skeleton className="h-64" />;

  return (
    <Card className="bg-card/50 backdrop-blur-sm border-border/50">
      <CardContent className="p-0">
        {/* Export Buttons */}
        <div className="p-4 border-b border-border/50 flex justify-between items-center">
          <span className="text-sm text-muted-foreground">
            {ledger?.entries?.length || 0} entries
          </span>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleExport('csv')}
              disabled={exporting || !ledger?.entries?.length}
              className="text-xs"
              data-testid="export-csv-btn"
            >
              {exporting === 'csv' ? (
                <Loader2 className="w-3 h-3 mr-1 animate-spin" />
              ) : (
                <FileText className="w-3 h-3 mr-1" />
              )}
              CSV
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleExport('xlsx')}
              disabled={exporting || !ledger?.entries?.length}
              className="text-xs"
              data-testid="export-xlsx-btn"
            >
              {exporting === 'xlsx' ? (
                <Loader2 className="w-3 h-3 mr-1 animate-spin" />
              ) : (
                <FileSpreadsheet className="w-3 h-3 mr-1" />
              )}
              Excel
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleExport('pdf')}
              disabled={exporting || !ledger?.entries?.length}
              className="text-xs"
              data-testid="export-pdf-btn"
            >
              {exporting === 'pdf' ? (
                <Loader2 className="w-3 h-3 mr-1 animate-spin" />
              ) : (
                <FileDown className="w-3 h-3 mr-1" />
              )}
              PDF
            </Button>
          </div>
        </div>
        
        <Table>
          <TableHeader>
            <TableRow className="border-border/50 hover:bg-transparent">
              <TableHead>Date</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Reference</TableHead>
              <TableHead className="text-right">Debit</TableHead>
              <TableHead className="text-right">Credit</TableHead>
              <TableHead className="text-right">Balance</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {ledger?.entries?.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                  No ledger entries found
                </TableCell>
              </TableRow>
            )}
            {ledger?.entries?.map((entry) => (
              <TableRow key={entry.reference_id || `${entry.date}-${entry.reference}-${entry.type}`} className="border-border/50 hover:bg-muted/50">
                <TableCell className="font-mono text-sm">{formatDate(entry.date)}</TableCell>
                <TableCell>
                  <Badge variant="outline" className={entry.type === 'BILL' ? 'border-rose-500/30 text-rose-400' : 'border-emerald-500/30 text-emerald-400'}>
                    {entry.type}
                  </Badge>
                </TableCell>
                <TableCell className="font-mono text-sm">{entry.reference}</TableCell>
                <TableCell className="text-right font-mono text-rose-400">
                  {entry.debit > 0 ? formatCurrency(entry.debit) : '-'}
                </TableCell>
                <TableCell className="text-right font-mono text-emerald-400">
                  {entry.credit > 0 ? formatCurrency(entry.credit) : '-'}
                </TableCell>
                <TableCell className="text-right font-mono font-medium">
                  {formatCurrency(entry.balance)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {ledger && (
          <div className="p-4 border-t border-border/50 flex justify-between items-center gap-3">
            <span className="text-muted-foreground">Closing Balance</span>
            <div className="flex items-center gap-3">
              {ledger.summary?.hard_duplicates_hidden > 0 && (
                <span
                  className="text-[10px] px-2 py-0.5 rounded border border-amber-500/40 bg-amber-500/10 text-amber-300"
                  title="Duplicate bills detected upstream and excluded from totals"
                  data-testid="ledger-dup-badge"
                >
                  {ledger.summary.hard_duplicates_hidden} duplicate{ledger.summary.hard_duplicates_hidden === 1 ? '' : 's'} hidden
                </span>
              )}
              <span
                className={`font-mono font-bold text-lg ${(ledger.summary?.outstanding ?? 0) > 0 ? 'text-amber-400' : 'text-emerald-400'}`}
                data-testid="ledger-closing-balance"
              >
                {formatCurrency(
                  ledger.summary?.outstanding
                    ?? ledger.entries?.[ledger.entries.length - 1]?.balance
                    ?? 0
                )}
              </span>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default VendorDetailPage;
